import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldSelect } from '../../../components';

import css from './ConfirmSignupForm.module.css';

const KEY_CODE_ENTER = 13;

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        onOpenTermsOfService,
        onOpenPrivacyPolicy,
        authInfo,
        idp,
      } = formRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'General.email',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // firstName
      const firstNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'General.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameRequired',
      });
      const firstNameRequired = validators.required(firstNameRequiredMessage);

      // lastName
      const lastNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'General.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameRequired',
      });
      const lastNameRequired = validators.required(lastNameRequiredMessage);
      
      // Company name
      const companyNameLabel = intl.formatMessage({
        id: 'General.companyNameLabel',
      });
      const companyNamePlaceholder = intl.formatMessage({
        id: 'General.companyNamePlaceholder',
      });
      const companyNameRequiredMessage = intl.formatMessage({
        id: 'General.companyNameRequired',
      });
      const companyNameRequired = validators.required(companyNameRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const handleTermsKeyUp = e => {
        // Allow click action with keyboard like with normal links
        if (e.keyCode === KEY_CODE_ENTER) {
          onOpenTermsOfService();
        }
      };
      const termsLink = (
        <span
          className={css.termsLink}
          onClick={onOpenTermsOfService}
          role="button"
          tabIndex="0"
          onKeyUp={handleTermsKeyUp}
        >
          <FormattedMessage id="ConfirmSignupForm.termsAndConditionsLinkText" />
        </span>
      );

      const privacyLink = (
        <span
          className={css.termsLink}
          onClick={onOpenPrivacyPolicy}
          role="button"
          tabIndex="0"
          onKeyUp={e => e.keyCode === KEY_CODE_ENTER && onOpenPrivacyPolicy()}
        >
          <FormattedMessage id="SignupForm.privacyPolicyLinkText" />
        </span>
      );

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName, userIntent } = authInfo;
      const isProvider = userIntent === 'provider';

      // Account type labels for providers
      const userTypeLabel = intl.formatMessage({
        id: 'SignupForm.userTypeLabel',
      });
      const userTypePlaceholder = intl.formatMessage({
        id: 'General.accountTypePlaceholder',
      });
      const userTypeIndividual = intl.formatMessage({
        id: 'SignupForm.userTypeIndividual',
      });
      const userTypeCompany = intl.formatMessage({
        id: 'SignupForm.userTypeCompany',
      });
      const userTypeRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.userTypeRequired' })
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const accountType = values.accountType;
              const isCompanyAccount = accountType === 'certifiedBusiness';
              
              return (
                <div>
                  <FieldTextInput
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={emailLabel}
                    placeholder={emailPlaceholder}
                    initialValue={email}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                  
                  {/* Account Type Selection for Providers - Show first */}
                  {isProvider && (
                    <div className={css.providerTypeField}>
                      <FieldSelect
                        id="accountType"
                        name="accountType"
                        label={userTypeLabel}
                        validate={userTypeRequired}
                      >
                        <option disabled value="">
                          {userTypePlaceholder}
                        </option>
                        <option value="certifiedProfessional">{userTypeIndividual}</option>
                        <option value="certifiedBusiness">{userTypeCompany}</option>
                      </FieldSelect>
                    </div>
                  )}
                  
                  {/* Show name fields for individuals, company name for businesses */}
                  {!isProvider || !isCompanyAccount ? (
                    <div className={css.name}>
                      <FieldTextInput
                        className={css.firstNameRoot}
                        type="text"
                        id={formId ? `${formId}.firstName` : 'firstName'}
                        name="firstName"
                        autoComplete="given-name"
                        label={firstNameLabel}
                        placeholder={firstNamePlaceholder}
                        initialValue={firstName}
                        validate={firstNameRequired}
                      />
                      <FieldTextInput
                        className={css.lastNameRoot}
                        type="text"
                        id={formId ? `${formId}.lastName` : 'lastName'}
                        name="lastName"
                        autoComplete="family-name"
                        label={lastNameLabel}
                        placeholder={lastNamePlaceholder}
                        initialValue={lastName}
                        validate={lastNameRequired}
                      />
                    </div>
                  ) : (
                    <FieldTextInput
                      className={css.companyNameField}
                      type="text"
                      id={formId ? `${formId}.companyName` : 'companyName'}
                      name="companyName"
                      label={companyNameLabel}
                      placeholder={companyNamePlaceholder}
                      validate={companyNameRequired}
                    />
                  )}
                </div>
              );
            }}
          </FormSpy>

          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
            </PrimaryButton>
            <p className={css.bottomWrapperText}>
              <span className={css.termsText}>
                <FormattedMessage
                  id="ConfirmSignupForm.termsAndConditionsAcceptText"
                  values={{ termsLink, privacyLink }}
                />
              </span>
            </p>
          </div>
        </Form>
      );
    }}
  />
);

ConfirmSignupFormComponent.defaultProps = { inProgress: false };

const { bool, func } = PropTypes;

ConfirmSignupFormComponent.propTypes = {
  inProgress: bool,

  onOpenTermsOfService: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmSignupForm = compose(injectIntl)(ConfirmSignupFormComponent);
ConfirmSignupForm.displayName = 'ConfirmSignupForm';

export default ConfirmSignupForm;
